
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { getTutorStudents, updateTutorStudentStatus, getTutorProfile, searchFriendById, addTutorStudent, deleteTutorStudent, getPublicSetting, getUserCourses, createTutorCourse, getTutorStudentCourseDrafts, moveDraftToCourse, getPracticeLanguages, createTutorStudentChatSession, deleteTutorCourse } from '../api/supabaseAPI';
import Loader from '../components/Loader';
import Message from '../components/Message';
import ToastStack from '../components/ToastStack';
import UserProfilePopover from '../components/UserProfilePopover';
import ConfirmDialog from '../components/ConfirmDialog'; // ✨ Import ConfirmDialog
import ManageStudentModal from '../components/ManageStudentModal'; // ✨ Import ManageStudentModal
import AssignCourseModal from '../components/AssignCourseModal'; // ✨ Import AssignCourseModal
import { ChatBubbleIcon, TrashIcon } from '../components/icons/ActionIcons';
import countriesData from '../list_data/countries_en.json';
import '../styles/AdminDashboard.css';
import '../styles/Friends.css';

const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

export default function TutorWorkspace() {
  const { t } = useTranslation();
  const session = useSession();
  const { language } = useParams();
  const userId = session?.user?.id;
  const supabase = useSupabaseClient();
  const queryClient = useQueryClient();
  
  // Tab state from URL
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'requests';
  
  // ✨ Sub-tab state for Requests (received | sent)
  const [requestSubTab, setRequestSubTab] = useState('received');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchAvatarError, setIsSearchAvatarError] = useState(false);

  // Popover state
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [viewedStudent, setViewedStudent] = useState(null);
  const [popoverAnchor, setPopoverAnchor] = useState({ x: 0, y: 0 });
  const [popoverRect, setPopoverRect] = useState({ width: 300, height: 360 });

  // Add student state
  const [addMessage, setAddMessage] = useState({ text: '', type: '' });
  const [creatingStudentChatId, setCreatingStudentChatId] = useState('');

  // ✨ Confirm Dialog state for canceling invite / removing student
  const [confirmDialog, setConfirmDialog] = useState({ 
    isOpen: false, 
    studentId: null, 
    relationshipId: null,
    type: 'cancel' // 'cancel' | 'remove'
  });

  // ✨ Manage Student Modal state
  const [manageModal, setManageModal] = useState({
    isOpen: false,
    student: null,
    courses: [],
    drafts: [],
  });

  // ✨ Assign Course Modal state
  const [assignModal, setAssignModal] = useState({
    isOpen: false,
    student: null,
    draft: null,
  });

  useEffect(() => {
    if (!addMessage.text) return undefined;
    const timer = setTimeout(() => {
      setAddMessage({ text: '', type: '' });
    }, 3000);
    return () => clearTimeout(timer);
  }, [addMessage]);

  // Fetch Tutor Profile (to verify access and get name)
  const { data: tutorProfile, isLoading: isProfileLoading } = useQuery({
    queryKey: ['tutorProfile', userId, language],
    queryFn: () => getTutorProfile(userId, language),
    enabled: !!userId && !!language,
  });

  // Fetch R2 public base URL for avatar/background images
  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });
  const { data: practiceLanguages = [], isLoading: isLoadingPracticeLanguages } = useQuery({
    queryKey: ['practiceLanguages'],
    queryFn: getPracticeLanguages,
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);

  const practiceLanguageMap = useMemo(() => (
    new Map(practiceLanguages.map((item) => [item.value, item.label]))
  ), [practiceLanguages]);

  const isExternalUrl = useCallback((value) => /^https?:\/\//i.test(value || ''), []);

  const searchAvatarUrl = useMemo(() => {
    const avatarPath = searchResult?.avatar;
    if (!avatarPath) return '';
    if (isExternalUrl(avatarPath)) return avatarPath;
    if (!publicBaseUrl) return avatarPath;
    return `${publicBaseUrl}/${avatarPath}`;
  }, [publicBaseUrl, searchResult?.avatar, isExternalUrl]);

  const searchBackgroundUrl = useMemo(() => {
    const backgroundPath = searchResult?.background;
    if (!backgroundPath) return '';
    if (isExternalUrl(backgroundPath)) return backgroundPath;
    if (!publicBaseUrl) return backgroundPath;
    return `${publicBaseUrl}/${backgroundPath}`;
  }, [publicBaseUrl, searchResult?.background, isExternalUrl]);

  const getAvatarUrl = useCallback((user) => {
    const avatarPath = user?.avatar;
    if (!avatarPath) return '';
    if (isExternalUrl(avatarPath)) return avatarPath;
    if (!publicBaseUrl) return avatarPath;
    return `${publicBaseUrl}/${avatarPath}`;
  }, [publicBaseUrl, isExternalUrl]);

  const getBackgroundUrl = useCallback((user) => {
    const backgroundPath = user?.background;
    if (!backgroundPath) return '';
    if (isExternalUrl(backgroundPath)) return backgroundPath;
    if (!publicBaseUrl) return backgroundPath;
    return `${publicBaseUrl}/${backgroundPath}`;
  }, [publicBaseUrl, isExternalUrl]);

  const updatePopoverPosition = useCallback((anchor, rect) => {
    const padding = 12;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const placements = [
      { key: 'bottom-right', fits: anchor.x + rect.width + padding <= viewportWidth && anchor.y + rect.height + padding <= viewportHeight },
      { key: 'bottom-left', fits: anchor.x - rect.width - padding >= 0 && anchor.y + rect.height + padding <= viewportHeight },
      { key: 'top-right', fits: anchor.x + rect.width + padding <= viewportWidth && anchor.y - rect.height - padding >= 0 },
      { key: 'top-left', fits: anchor.x - rect.width - padding >= 0 && anchor.y - rect.height - padding >= 0 },
    ];

    const chosen = placements.find((placement) => placement.fits) || placements[0];
    let left = anchor.x + padding;
    let top = anchor.y + padding;

    if (chosen.key.includes('left')) {
      left = anchor.x - rect.width - padding;
    }
    if (chosen.key.includes('top')) {
      top = anchor.y - rect.height - padding;
    }

    document.documentElement.style.setProperty('--friend-popover-left', `${Math.max(padding, left)}px`);
    document.documentElement.style.setProperty('--friend-popover-top', `${Math.max(padding, top)}px`);
    document.documentElement.style.setProperty('--friend-popover-placement', chosen.key);
  }, []);

  const handlePopoverMeasure = useCallback((rect) => {
    setPopoverRect((prev) => {
      if (prev.width === rect.width && prev.height === rect.height) {
        return prev;
      }
      return { width: rect.width, height: rect.height };
    });
    updatePopoverPosition(popoverAnchor, { width: rect.width, height: rect.height });
  }, [popoverAnchor, updatePopoverPosition]);

  useEffect(() => {
    if (!isPopoverOpen) return;
    updatePopoverPosition(popoverAnchor, popoverRect);
  }, [isPopoverOpen, popoverAnchor, popoverRect, updatePopoverPosition]);

  useEffect(() => {
    if (!isPopoverOpen) return;
    const handleViewportChange = () => {
      updatePopoverPosition(popoverAnchor, popoverRect);
    };
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isPopoverOpen, popoverAnchor, popoverRect, updatePopoverPosition]);

  // Fetch Students (filter by current language)
  const { data: allStudents, isLoading: isStudentsLoading } = useQuery({
    queryKey: ['tutorStudents', userId, language],
    queryFn: () => getTutorStudents(userId, language),
    enabled: !!userId && !!language,
  });

  const activeStudents = useMemo(() =>  
    allStudents?.filter(s => s.status === 'accepted') || [], 
  [allStudents]);

  // ✨ Prefetch Courses for Active Students
  // This query runs in background and caches results
  const { data: prefetchedCourses } = useQuery({
    queryKey: ['allStudentCourses', userId, language],
    queryFn: async () => {
      if (!activeStudents || activeStudents.length === 0) return {};
      
      const coursesMap = {};
      // Fetch courses for all active students in parallel
      await Promise.all(
        activeStudents.map(async (student) => {
          try {
            const courses = await getUserCourses(student.student_id, language);
            coursesMap[student.student_id] = courses || [];
          } catch (err) {
            console.error(`Failed to prefetch courses for student ${student.student_id}`, err);
            coursesMap[student.student_id] = [];
          }
        })
      );
      return coursesMap;
    },
    enabled: activeStudents.length > 0 && !!language,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on focus to keep cache stable
    refetchOnMount: true // Fetch on mount
  });

  // ✨ Realtime Subscription
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`tutor-workspace-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'tutor_students',
          filter: `tutor_id=eq.${userId}`, // Filter for this tutor's records
        },
        () => {
          // Invalidate and refetch whenever a change occurs
          queryClient.invalidateQueries({ queryKey: ['tutorStudents', userId, language] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, language, queryClient, supabase]);

  // Actions
  const { mutate: updateStatus, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, status }) => updateTutorStudentStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tutorStudents', userId, language] });
    },
  });

  // Add student mutation
  const { mutate: addStudent, isPending: isAdding } = useMutation({
    mutationFn: ({ studentId }) => addTutorStudent(userId, studentId, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tutorStudents', userId, language] });
      setAddMessage({ text: 'Student added successfully! Waiting for their confirmation.', type: 'success' });
      // Update search result to show new status
      if (searchResult) {
        setSearchResult({ ...searchResult, existingStatus: 'pending' });
      }
    },
    onError: (error) => {
      setAddMessage({ text: error.message || 'Failed to add student.', type: 'error' });
    },
  });

  // ✨ Delete student mutation (Cancel Invite)
  const { mutate: deleteStudent, isPending: isDeleting } = useMutation({
    mutationFn: (id) => deleteTutorStudent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tutorStudents', userId, language] });
    },
    onError: (error) => {
      // You might want to show a toast or message here
      console.error("Failed to cancel invite:", error);
    }
  });

  const { mutateAsync: removeCourse } = useMutation({
    mutationFn: (courseId) => deleteTutorCourse(courseId),
  });

  const pendingStudents = useMemo(() =>  
    allStudents?.filter(s => s.status === 'pending') || [], 
  [allStudents]);

  // ✨ Split pending into Received vs Sent
  const receivedRequests = useMemo(() => 
    pendingStudents.filter(s => s.action_by === 'student'), 
  [pendingStudents]);

  const sentInvitations = useMemo(() => 
    pendingStudents.filter(s => s.action_by === 'tutor'), 
  [pendingStudents]);

  const getLanguageDisplayName = (lang) => {
    return practiceLanguageMap.get(lang) || lang;
  };

  const getCountryName = (code) => {
    if (!code) return 'Unknown';
    const found = countriesData.find(c => c.name === code || c.code === code);
    return found ? found.name : code;
  };

  const calculateAge = (birthdayString) => {
    if (!birthdayString) return 'N/A';
    const birthday = new Date(birthdayString);
    const today = new Date();
    let age = today.getFullYear() - birthday.getFullYear();
    const m = today.getMonth() - birthday.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthday.getDate())) {
        age--;
    }
    return age;
  };

  const handleViewProfile = (student, event) => {
    if (event) {
      // Viewed from Requests/Students list
      setPopoverAnchor({ x: event.clientX, y: event.clientY });
      setViewedStudent(student);
      setIsPopoverOpen(true);
    } else {
      // Viewed from search result
      setViewedStudent(null);
      setIsPopoverOpen(true);
    }
  };

  const handleAddStudent = (studentId) => {
    setAddMessage({ text: '', type: '' });
    addStudent({ studentId });
  };

  // ✨ Handle Delete/Cancel Click
  const handleDeleteClick = (studentId, relationshipId, type) => {
    setConfirmDialog({
      isOpen: true,
      studentId,
      relationshipId,
      type
    });
  };

  // ✨ Confirm Deletion
  const handleConfirmDelete = () => {
    if (confirmDialog.relationshipId) {
      deleteStudent(confirmDialog.relationshipId);
    }
    setConfirmDialog({ isOpen: false, studentId: null, relationshipId: null, type: 'cancel' });
  };

  // ✨ Handle Manage Student Click
  const handleManageStudent = async (student) => {
    // 1. Try to get from prefetched cache first
    const cachedCourses = prefetchedCourses?.[student.student_id];
    
    if (cachedCourses) {
      setManageModal({
        isOpen: true,
        student: student,
        courses: cachedCourses,
        drafts: [],
      });
      // Optionally re-fetch in background to ensure freshness
      getUserCourses(student.student_id, language).then(freshCourses => {
         // Deep comparison to avoid unnecessary re-renders
         if (JSON.stringify(freshCourses) !== JSON.stringify(cachedCourses)) {
            setManageModal(prev => prev.isOpen && prev.student?.student_id === student.student_id ? {
                ...prev,
                courses: freshCourses
            } : prev);
         }
      });
      getTutorStudentCourseDrafts(userId, student.student_id, language).then(freshDrafts => {
        setManageModal(prev => prev.isOpen && prev.student?.student_id === student.student_id ? {
          ...prev,
          drafts: freshDrafts || [],
        } : prev);
      }).catch((err) => {
        console.error('Failed to fetch course drafts:', err);
      });
    } else {
      // 2. Fallback to direct fetch if not cached
      try {
        const [courses, drafts] = await Promise.all([
          getUserCourses(student.student_id, language),
          getTutorStudentCourseDrafts(userId, student.student_id, language),
        ]);
        setManageModal({
          isOpen: true,
          student: student,
          courses: courses || [],
          drafts: drafts || [],
        });
      } catch (error) {
        console.error("Failed to fetch student courses:", error);
        setAddMessage({ text: 'Failed to load student courses.', type: 'error' });
        // Still open modal but with empty courses
        setManageModal({
          isOpen: true,
          student: student,
          courses: [],
          drafts: [],
        });
      }
    }
  };

  // ✨ Handle Assign Course (opens second modal)
  const handleAssignCourse = (draft = null) => {
    const normalizedDraft = draft && typeof draft === 'object' && Object.prototype.hasOwnProperty.call(draft, 'id')
      ? draft
      : null;
    setAssignModal({
      isOpen: true,
      student: manageModal.student,
      draft: normalizedDraft,
    });
  };

  // ✨ Handle Course Assignment Submission
  const handleSubmitAssignment = async (payload) => {
    try {
      if (payload.type === 'ai' && payload.draftId) {
        await moveDraftToCourse({
          draftId: payload.draftId,
          courseName: payload.courseName,
          description: payload.description,
          sentences: payload.sentences,
        });
      } else {
        await createTutorCourse(userId, payload.studentId, {
          courseName: payload.courseName,
          description: payload.description,
          sentences: payload.sentences,
          language: language // Pass current workspace language
        });
      }

      // Show success message
      setAddMessage({ text: `Course "${payload.courseName}" assigned successfully!`, type: 'success' });
      
      // Refresh student courses + drafts
      const [updatedCourses, updatedDrafts] = await Promise.all([
        getUserCourses(payload.studentId, language),
        getTutorStudentCourseDrafts(userId, payload.studentId, language),
      ]);
      setManageModal(prev => ({
        ...prev,
        courses: updatedCourses || [],
        drafts: updatedDrafts || [],
      }));

    } catch (error) {
      console.error("Failed to assign course:", error);
      setAddMessage({ text: `Failed to assign course: ${error.message}`, type: 'error' });
      throw error; // Re-throw to let the modal know it failed (if needed)
    }
  };

  // ✨ Handle Remove Course
  const handleRemoveCourse = async (course) => {
    const courseId = String(course?.cid || '').trim();
    const studentId = manageModal.student?.student_id || course?.uid || '';
    const courseName = course?.title || course?.course_name || 'Course';

    if (!courseId) {
      throw new Error('Course ID is missing.');
    }

    try {
      await removeCourse(courseId);
      const updatedCourses = studentId ? await getUserCourses(studentId, language) : [];

      setManageModal((prev) => (
        prev.isOpen && prev.student?.student_id === studentId
          ? { ...prev, courses: updatedCourses || [] }
          : prev
      ));

      queryClient.invalidateQueries({ queryKey: ['allStudentCourses', userId, language] });
      setAddMessage({ text: `Course "${courseName}" removed successfully.`, type: 'success' });
    } catch (error) {
      console.error('Failed to remove course:', error);
      setAddMessage({ text: error.message || 'Failed to remove course.', type: 'error' });
      throw error;
    }
  };

  // ✨ Handle View Course
  const handleViewCourse = (course) => {
    // TODO: Implement course view
    console.log('View course:', course);
  };

  const handleOpenStudentChat = async (student) => {
    const relationshipId = String(student?.relationship_id || '').trim();
    if (!relationshipId) {
      setAddMessage({ text: 'Student relationship is missing. Please refresh and try again.', type: 'error' });
      return;
    }

    setCreatingStudentChatId(relationshipId);
    try {
      const result = await createTutorStudentChatSession(relationshipId, student?.student_id || '');
      window.dispatchEvent(new CustomEvent('theralingua:open-chat-panel', {
        detail: {
          sectionKey: 'my_students',
          sessionId: result?.session_id || '',
          tutorStudentId: relationshipId,
        },
      }));
      setAddMessage({
        text: result?.created_new ? 'Student chat session created.' : 'Student chat session is ready.',
        type: 'success',
      });
    } catch (error) {
      setAddMessage({
        text: error instanceof Error ? error.message : 'Failed to open student chat.',
        type: 'error',
      });
    } finally {
      setCreatingStudentChatId('');
    }
  };

  const handleCloseAssignModal = async () => {
    const currentStudentId = assignModal.student?.student_id || manageModal.student?.student_id;
    setAssignModal({ isOpen: false, student: null, draft: null });

    if (!currentStudentId || !userId) return;
    try {
      const refreshedDrafts = await getTutorStudentCourseDrafts(userId, currentStudentId, language);
      setManageModal((prev) => (
        prev.student?.student_id === currentStudentId
          ? { ...prev, drafts: refreshedDrafts || [] }
          : prev
      ));
    } catch (error) {
      console.error('Failed to refresh drafts on closing assign modal:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      setSearchError('Please enter a user ID to search.');
      return;
    }
    
    setIsSearching(true);
    setSearchError('');
    setSearchResult(null);
    
    try {
      const result = await searchFriendById(trimmedQuery);
      if (result) {
        // Check if this student is already connected
        const existingRelation = allStudents?.find(s => s.student_id === result.id);
        setSearchResult({ ...result, existingStatus: existingRelation?.status || null });
      } else {
        setSearchError('No user found with this ID.');
      }
    } catch (err) {
      setSearchError(err.message || 'Search failed.');
    } finally {
      setIsSearching(false);
    }
  };

  if (isProfileLoading || isStudentsLoading || isLoadingPracticeLanguages) return <main className="admin-container"><Loader /></main>;

  if (!tutorProfile) {
    return (
      <main className="admin-container">
        <Message text="Tutor profile not found for this language." type="error" />
      </main>
    );
  }

  return (
    <main className="admin-container">
      <ToastStack items={[addMessage]} />
      <header className="admin-header">
        <h1 className="admin-title">
          {getLanguageDisplayName(language)} Tutor Workspace
        </h1>
        <p style={{ color: 'var(--text-medium)', marginBottom: '16px' }}>
          Welcome, {tutorProfile.display_name || tutorProfile.full_name}.
        </p>
        
        <div className="admin-tabs">
          <button 
            className={`admin-tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setSearchParams({ tab: 'requests' })}
          >
            <span>Requests</span>
            {pendingStudents.length > 0 ? (
              <span className="tab-count-badge">{pendingStudents.length}</span>
            ) : null}
          </button>
          <button 
            className={`admin-tab-btn ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setSearchParams({ tab: 'students' })}
          >
            <span>My Students</span>
            {activeStudents.length > 0 ? (
              <span className="tab-count-badge">{activeStudents.length}</span>
            ) : null}
          </button>
          <button 
            className={`admin-tab-btn ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => setSearchParams({ tab: 'search' })}
          >
            Find Student
          </button>
        </div>
      </header>

      {/* Pending Requests Tab */}
      {activeTab === 'requests' && (
        <section className="admin-section">
          {/* ✨ Sub Tabs */}
          <div className="admin-sub-tabs">
            <button 
              className={`sub-tab-btn ${requestSubTab === 'received' ? 'active' : ''}`}
              onClick={() => setRequestSubTab('received')}
            >
              <span>Received Requests</span>
              {receivedRequests.length > 0 ? (
                <span className="tab-count-badge">{receivedRequests.length}</span>
              ) : null}
            </button>
            <button 
              className={`sub-tab-btn ${requestSubTab === 'sent' ? 'active' : ''}`}
              onClick={() => setRequestSubTab('sent')}
            >
              <span>Sent Invitations</span>
            </button>
          </div>

          {/* ✨ Received Requests List */}
          {requestSubTab === 'received' && (
            <>
              {receivedRequests.length === 0 ? (
                <div className="empty-state">No pending requests from students.</div>
              ) : (
                <div className="admin-list-grid">
                  {receivedRequests.map(student => (
                    <div key={student.relationship_id} className="application-card">
                      <div className="app-card-header">
                        <div className="app-applicant">
                           <img src={getAvatarUrl(student) || '/Resources/imagine/default_avatar/default_avatar.jpeg'} alt="Avatar" className="app-avatar" />
                           <div className="app-info">
                             <h3>{student.username}</h3>
                             <p>{getCountryName(student.country)} • {student.native_language}</p>
                           </div>
                        </div>
                      </div>
                      <div className="app-content">
                        <p><strong>Goal:</strong> {student.learning_goal || 'Not specified'}</p>
                      </div>
                      <div className="app-actions">
                        <button
                          className="friends-btn ghost"
                          type="button"
                          onClick={(event) => handleViewProfile(student, event)}
                        >
                          View
                        </button>
                        <button 
                          className="reject-btn"
                          disabled={isUpdating}
                          onClick={() => updateStatus({ id: student.relationship_id, status: 'rejected' })}
                        >
                          Reject
                        </button>
                        <button 
                          className="approve-btn"
                          disabled={isUpdating}
                          onClick={() => updateStatus({ id: student.relationship_id, status: 'accepted' })}
                        >
                          Accept
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ✨ Sent Invitations List */}
          {requestSubTab === 'sent' && (
            <>
              {sentInvitations.length === 0 ? (
                <div className="empty-state">No pending invitations sent.</div>
              ) : (
                <div className="admin-list-grid">
                  {sentInvitations.map(student => (
                    <div key={student.relationship_id} className="application-card">
                      <div className="app-card-header">
                        <div className="app-applicant">
                           <img src={getAvatarUrl(student) || '/Resources/imagine/default_avatar/default_avatar.jpeg'} alt="Avatar" className="app-avatar" />
                           <div className="app-info">
                             <h3>{student.username}</h3>
                             <p>{getCountryName(student.country)} • {student.native_language}</p>
                           </div>
                        </div>
                        <span className="lang-badge" style={{ background: 'var(--primary-color)', color: 'white' }}>
                          Invited
                        </span>
                      </div>
                      <div className="app-content">
                        <p><strong>Goal:</strong> {student.learning_goal || 'Not specified'}</p>
                      </div>
                      <div className="app-actions">
                        <button
                          className="friends-btn ghost"
                          type="button"
                          onClick={(event) => handleViewProfile(student, event)}
                        >
                          View
                        </button>
                        {/* ✨ Cancel Button for Sent Invites */}
                        <button 
                          className="cancel-invite-btn"
                          disabled={isUpdating || isDeleting}
                          onClick={() => handleDeleteClick(student.student_id, student.relationship_id, 'cancel')}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* ✨ Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.type === 'remove' ? "Remove Student" : "Cancel Invitation"}
        message={
          confirmDialog.type === 'remove' 
            ? "Are you sure you want to remove this student? They will no longer be listed as your student." 
            : "Are you sure you want to cancel this invitation? The student will no longer see your request."
        }
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDialog({ isOpen: false, studentId: null, relationshipId: null, type: 'cancel' })}
      />

      {/* My Students Tab */}
      {activeTab === 'students' && (
        <section className="admin-section">
          {activeStudents.length === 0 ? (
            <div className="empty-state">You don't have any active students yet.</div>
          ) : (
            <div className="admin-list-grid">
              {activeStudents.map(student => (
                <div key={student.relationship_id} className="application-card">
                  <div className="app-card-header">
                    <div className="app-applicant">
                      <img 
                        src={getAvatarUrl(student) || '/Resources/imagine/default_avatar/default_avatar.jpeg'} 
                        alt="Avatar" 
                        className="app-avatar" 
                        style={{objectFit: 'cover'}}
                      />
                      <div className="app-info">
                        <h3>{student.username}</h3>
                        <p className="student-learning-goal">
                          {student.learning_goal || 'No learning goal specified'}
                        </p>
                      </div>
                    </div>
                    <span className="lang-badge student-country-badge">
                      {getCountryName(student.country)}
                    </span>
                  </div>
                  
                  <div className="student-card-footer">
                    <span className="student-age">
                      Age: {calculateAge(student.birthday)}
                    </span>
                    <div className="student-actions">
                      <button 
                        className="view-btn student-action-btn" 
                        onClick={() => handleManageStudent(student)}
                      >
                        Manage
                      </button>
                      <button 
                        className="view-btn student-action-btn" 
                        onClick={(event) => handleViewProfile(student, event)}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className="student-icon-btn student-chat-btn"
                        onClick={() => handleOpenStudentChat(student)}
                        title="Open chat"
                        aria-label={`Open chat with ${student.username || 'this student'}`}
                        disabled={creatingStudentChatId === student.relationship_id}
                      >
                        <ChatBubbleIcon size={18} />
                      </button>
                      <button 
                        type="button"
                        className="student-icon-btn student-icon-danger"
                        onClick={() => handleDeleteClick(student.student_id, student.relationship_id, 'remove')}
                        disabled={isDeleting}
                        title="Remove student"
                        aria-label={`Remove ${student.username || 'this student'}`}
                      >
                        <TrashIcon size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Find Student Tab */}
      {activeTab === 'search' && (
        <section className="admin-section">
          <div className="friends-card">
            <header className="card-header">
              <h2 className="card-title">Find Student</h2>
            </header>
            <div className="friends-search">
              <form className="search-form" onSubmit={handleSearch}>
                <div className="search-input-wrap">
                  <span className="search-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="11" cy="11" r="7" />
                      <path d="M20 20l-3.5-3.5" />
                    </svg>
                  </span>
                  <input
                    className="search-input"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by user ID"
                  />
                </div>
                <button className="friends-btn search-btn" type="submit" disabled={isSearching}>
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
              </form>

              {addMessage.text && (
                <Message text={addMessage.text} type={addMessage.type} />
              )}

              {searchError && (
                <div className="search-results">
                  <p className="search-message error">{searchError}</p>
                </div>
              )}

              {searchResult && (
                <div className="search-results has-result">
                  <div className="friend-item search-result-item">
                    <div className="friend-avatar">
                      {getAvatarUrl(searchResult) ? (
                        <img
                          className="friend-avatar-img is-visible"
                          src={getAvatarUrl(searchResult)}
                          alt="User avatar"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <span className="friend-avatar-initial">
                          {(searchResult.username || '?')[0].toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="friend-info">
                      <div className="friend-name">{searchResult.username || 'Unknown'}</div>
                      <div className="friend-id">{getCountryName(searchResult.country)}</div>
                    </div>
                    <div className="friend-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        className="friends-btn ghost"
                        type="button"
                        onClick={(event) => handleViewProfile(searchResult, event)}
                      >
                        View
                      </button>
                      {searchResult.existingStatus ? (
                        <span className="lang-badge" style={{
                          background: searchResult.existingStatus === 'accepted' ? 'var(--success-color)' : 
                                      searchResult.existingStatus === 'pending' ? 'var(--warning-color)' : 
                                      'var(--text-tertiary)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: 600
                        }}>
                          {searchResult.existingStatus === 'accepted' ? 'Student' : 
                           searchResult.existingStatus === 'pending' ? 'Pending' : 
                           searchResult.existingStatus}
                        </span>
                      ) : (
                        <button
                          className="friends-btn primary"
                          type="button"
                          disabled={isAdding}
                          onClick={() => handleAddStudent(searchResult.id)}
                        >
                          {isAdding ? 'Adding...' : 'Add'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!searchResult && !searchError && (
                <div className="search-results">
                  <p className="search-empty">Search for a student by their user ID to view their profile.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* User Profile Popover */}
      {isPopoverOpen && (viewedStudent || searchResult) && (
          <UserProfilePopover
            userData={{
              username: (viewedStudent || searchResult).username,
              country: (viewedStudent || searchResult).country,
              learning_goal: (viewedStudent || searchResult).learning_goal,
              avatarUrl: viewedStudent ? getAvatarUrl(viewedStudent) : searchAvatarUrl,
              backgroundUrl: viewedStudent ? getBackgroundUrl(viewedStudent) : searchBackgroundUrl,
            }}
            showLearningGoal={true}
            onClose={() => {
            setIsPopoverOpen(false);
            setViewedStudent(null);
          }}
          showEmail={false}
          showActions={false}
          showCountry={true}
          variant="friends"
          onMeasure={handlePopoverMeasure}
        />
      )}

      {/* ✨ Manage Student Modal */}
      <ManageStudentModal
        isOpen={manageModal.isOpen}
        onClose={() => setManageModal({ isOpen: false, student: null, courses: [], drafts: [] })}
        student={manageModal.student}
        tutorId={userId}
        courses={manageModal.courses}
        drafts={manageModal.drafts}
        onAssignCourse={handleAssignCourse}
        onOpenDraft={handleAssignCourse}
        onRemoveCourse={handleRemoveCourse}
        onViewCourse={handleViewCourse}
        getAvatarUrl={getAvatarUrl}
      />

      {/* ✨ Assign Course Modal */}
      <AssignCourseModal
        isOpen={assignModal.isOpen}
        onClose={handleCloseAssignModal}
        student={assignModal.student}
        initialDraft={assignModal.draft}
        language={language}
        onAssign={handleSubmitAssignment}
      />
    </main>
  );
}
